import { useState } from 'react';

import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Tooltip } from 'antd';
import { useMessageLogger } from 'hooks/useMessageLogger';
import { type User, type UserPermissions } from 'models/user';
import { usersApi } from 'services/rtk/users';

import { GlobalPermissionRow } from './global-permission-row/GlobalPermissionRow';

type GlobalPermission = keyof Omit<UserPermissions, 'keitaro' | 'limitAccountPurchase'>;

const generalPermissions: Partial<Record<GlobalPermission, string>> = {
  canViewTrackers: 'Отображение трекеров',
  viewVideosPage: 'Страница видео',
};

const rolePermissions: Partial<Record<User['role'], Partial<Record<GlobalPermission, string>>>> = {
  PARTNER: {
    canSeeOfferSid: 'Отображение SID оффера',
    viewTasksBoardPage: 'Доска задач',
    viewCapiPage: 'Страницы capi',
    viewCandidatesPage: 'Страница кандидатов',
  },
  HEAD: {
    canSeeOfferSid: 'Отображение SID оффера',
    viewTasksBoardPage: 'Доска задач',
    viewCapiPage: 'Страницы capi',
    viewCandidatesPage: 'Страница кандидатов',
  },
  TEAM_LEAD: {
    canSeeOfferSid: 'Отображение SID оффера',
    viewTasksBoardPage: 'Доска задач',
    viewCapiPage: 'Страницы capi',
    viewCandidatesPage: 'Страница кандидатов',
  },
  BUYER: {
    canSeeOfferSid: 'Отображение SID оффера',
    viewTasksBoardPage: 'Доска задач',
    viewCapiPage: 'Страницы capi',
    viewCandidatesPage: 'Страница кандидатов',
  },
  BIZDEV: {
    canManageAffiliates: 'Создание/редактирование ПП и категорий',
    viewTasksBoardPage: 'Доска задач',
    viewTasksBoardStatsPage: 'Доска задач (статистика)',
    viewUsersPage: 'Страница пользователей',
    viewCandidatesPage: 'Страница кандидатов',
  },
  MANAGER: {
    viewUsersPage: 'Страница пользователей',
    viewCandidatesPage: 'Страница кандидатов',
  },
};

export const getPermissionMap = (userRole: User['role']): Partial<Record<GlobalPermission, string>> => {
  return {
    ...generalPermissions,
    ...rolePermissions[userRole],
  };
};

type PermissionsState = Record<GlobalPermission, boolean>;

const initPermissionsState = (permissions: UserPermissions) => ({
  canViewTrackers: Boolean(permissions.canViewTrackers),
  viewVideosPage: Boolean(permissions.viewVideosPage),
  viewTasksBoardPage: Boolean(permissions.viewTasksBoardPage),
  viewTasksBoardStatsPage: Boolean(permissions.viewTasksBoardStatsPage),
  viewUsersPage: Boolean(permissions.viewUsersPage),
  viewCapiPage: Boolean(permissions.viewCapiPage),
  viewCandidatesPage: Boolean(permissions.viewCandidatesPage),
  canSeeOfferSid: Boolean(permissions.canSeeOfferSid),
  canManageAffiliates: Boolean(permissions.canManageAffiliates),
});

interface Props {
  user: User;
  permissions: UserPermissions;
}

export const GlobalPermissions = ({ user, permissions }: Props) => {
  const { id: userId, role: userRole } = user;

  const [permissionState, setPermissionsState] = useState<PermissionsState>(() =>
    initPermissionsState(permissions)
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isStableState, setIsStableState] = useState(false);

  const [updatePermissions, updatePermissionsConfig] = usersApi.useUpdateUserPermissionsMutation();

  const handlePermissionChange = (permission: GlobalPermission) => (value: boolean) => {
    setPermissionsState((prev) => ({ ...prev, [permission]: value }));
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setIsStableState(false);
  };

  const handleSaveClick = () => {
    updatePermissions({ id: userId, ...permissionState });
  };

  const handleCancelClick = () => {
    setPermissionsState(initPermissionsState(permissions));
    setIsEditMode(false);
  };

  useMessageLogger({
    status: updatePermissionsConfig.status,
    error: updatePermissionsConfig.error,
    successMessage: 'Права пользователя обновлены',
    onSuccess: () => {
      setIsEditMode(false);
      setIsStableState(true);
    },
  });

  const permissionMap = getPermissionMap(userRole);

  return (
    <Card style={{ position: 'relative', background: 'transparent' }}>
      <Flex vertical gap={10}>
        {(Object.keys(permissionMap) as GlobalPermission[]).map((permission) => (
          <GlobalPermissionRow
            key={permission}
            label={permissionMap[permission] || ''}
            staticValue={isStableState ? permissionState[permission] : Boolean(permissions[permission])}
            value={permissionState[permission]}
            editMode={isEditMode}
            onChange={handlePermissionChange(permission)}
          />
        ))}
      </Flex>

      <Flex style={{ position: 'absolute', top: 10, right: 24 }} gap={8}>
        {isEditMode ? (
          <>
            <Tooltip style={{ padding: '8px' }} title="Сохранить" mouseEnterDelay={0.4} key="tooltip-save">
              <Button
                icon={<CheckOutlined />}
                onClick={handleSaveClick}
                loading={updatePermissionsConfig.isLoading}
                disabled={updatePermissionsConfig.isLoading}
              />
            </Tooltip>
            <Tooltip style={{ padding: '8px' }} title="Отмена" mouseEnterDelay={0.4} key="tooltip-cancel">
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancelClick}
                disabled={updatePermissionsConfig.isLoading}
                danger
              />
            </Tooltip>
          </>
        ) : (
          <Tooltip style={{ padding: '8px' }} title="Редактировать" mouseEnterDelay={0.4} key="tooltip-edit">
            <Button icon={<EditOutlined />} onClick={handleEditClick} />
          </Tooltip>
        )}
      </Flex>
    </Card>
  );
};
